import type { ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';

import { isTapPointer, nextTooltipOpen } from './infoTipState';

interface InfoTipProps {
  /** "?" 버튼의 접근성 이름. 예: "세션 제어 설명". */
  label: string;
  /** 툴팁 본문(KO 평이체). */
  children: ReactNode;
}

/**
 * 재사용 "?" 도움말 툴팁(#13).
 * - 데스크탑: 마우스 hover로 열림/닫힘
 * - 모바일: 탭으로 토글
 * - esc / 바깥 클릭으로 닫힘
 * - 열렸을 때 버튼에 aria-describedby로 툴팁을 연결
 */
export const InfoTip = ({ label, children }: InfoTipProps) => {
  const [open, setOpen] = useState(false);
  const tipId = useId();
  const rootRef = useRef<HTMLSpanElement>(null);
  // 클릭 직전 pointerType을 기억해 마우스 클릭(=hover가 처리)과 탭을 구분한다.
  const lastPointerType = useRef('');

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen((prev) => nextTooltipOpen(prev, { type: 'escape' }));
      }
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen((prev) => nextTooltipOpen(prev, { type: 'outside' }));
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  return (
    <span ref={rootRef} className="relative inline-flex">
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? tipId : undefined}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') {
            setOpen((prev) => nextTooltipOpen(prev, { type: 'pointerEnter' }));
          }
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === 'mouse') {
            setOpen((prev) => nextTooltipOpen(prev, { type: 'pointerLeave' }));
          }
        }}
        onPointerDown={(event) => {
          lastPointerType.current = event.pointerType;
        }}
        onClick={() => {
          if (isTapPointer(lastPointerType.current)) {
            setOpen((prev) => nextTooltipOpen(prev, { type: 'tap' }));
          }
          lastPointerType.current = '';
        }}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-border/60 text-[10px] font-bold leading-none text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        ?
      </button>
      {open && (
        <span
          role="tooltip"
          id={tipId}
          className="absolute left-1/2 top-full z-20 mt-1.5 w-52 -translate-x-1/2 rounded-lg border border-border/60 bg-popover px-3 py-2 text-xs font-normal leading-relaxed text-popover-foreground shadow-lg"
        >
          {children}
        </span>
      )}
    </span>
  );
};
